import type { OpLevel } from "@/config/operatingPoints";

export interface NeuroPrediction {
  predicted_class: boolean | null;
  operating_points: Record<OpLevel, boolean>;
  /** Producer-emitted DICOM filename; it need not equal the canonical SOP UID. */
  top_slice?: string | null;
}

export interface NeuroReview {
  successful_inference: boolean;
  study_gate: {
    selected_operating_point: OpLevel;
    operating_points: Record<OpLevel, boolean>;
  };
  predictions: Record<string, NeuroPrediction> | null;
}

export function isNeuroModel(model: unknown): boolean {
  return typeof model === "string" && model.startsWith("a2z-neuro-occlusion-v");
}

export function neuroOutcome(result: NeuroReview): string {
  const gate = result.study_gate;
  if (!gate.operating_points[gate.selected_operating_point]) return "Study incompatible";
  if (!result.successful_inference) return "Inference unsuccessful";
  const predictions = Object.values(result.predictions ?? {});
  const positive = predictions.filter((p) => p.predicted_class === true).length;
  const unavailable = predictions.filter((p) => p.predicted_class === null).length;
  return `${positive} positive · ${unavailable} no decision`;
}

export function neuroFindingLabel(condition: string): string {
  return condition
    .split("/")
    .filter((_, index) => index % 2 === 1)
    .join(" · ")
    .replaceAll("_", " ");
}

/** Worklist presentation derived from the emitted decisions, never detected_at_* lists. */
export interface NeuroWorklistSummary {
  label: string;
  findings: string[];
  count: number | null;
}

export function summarizeNeuroResult(result: NeuroReview | undefined): NeuroWorklistSummary {
  if (!result) return { label: "Results unavailable", findings: [], count: null };
  const accepted = result.study_gate.operating_points[result.study_gate.selected_operating_point];
  if (!accepted || !result.successful_inference) {
    return { label: neuroOutcome(result), findings: [], count: null };
  }
  if (!result.predictions || Object.keys(result.predictions).length === 0) {
    return { label: "Results unavailable", findings: [], count: null };
  }
  const findings = Object.entries(result.predictions)
    .filter(([, prediction]) => prediction.predicted_class === true)
    .map(([condition]) => condition);
  return { label: neuroOutcome(result), findings, count: findings.length };
}
